/**
 * The selected time window, shared by every range-scoped panel.
 *
 * 1h is deliberately absent. The ES indices are written by Logstash in batches,
 * so an hour is frequently empty and reads as an outage when it is only cadence.
 * A day is the shortest window that is always meaningful.
 *
 * The set is fetched from the server (`/metrics/ranges`) rather than hardcoded,
 * so a chip can never offer a window the server does not cache — that would
 * silently pay the full aggregation cost on every poll.
 */
import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { get } from './client'

export const Ranges = z.object({
  days: z.array(z.number()),
  ttl_s: z.record(z.string(), z.number()),
  default: z.number(),
})

/** Used only if `/metrics/ranges` is unreachable, so the board still renders
 *  chips instead of an empty toolbar. Typed as the schema so indexing it is not
 *  a union lookup. */
const FALLBACK: z.infer<typeof Ranges> = {
  days: [1, 2, 7, 30],
  ttl_s: { '1': 300, '2': 600, '7': 1800, '30': 3600 },
  default: 7,
}

type Ctx = {
  days: number
  setDays: (d: number) => void
  options: number[]
  /** server-side cache TTL for the selected window, in seconds */
  ttlSeconds: number
}

const RangeCtx = createContext<Ctx | null>(null)

export function RangeProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ['ranges'],
    queryFn: ({ signal }) => get('/metrics/ranges', Ranges, signal),
    // The window set changes only on deploy.
    staleTime: Infinity,
    retry: 1,
  })
  const cfg = data ?? FALLBACK
  const [days, setDays] = useState<number | null>(null)
  const selected = days ?? cfg.default

  const value = useMemo<Ctx>(() => ({
    days: selected,
    setDays,
    options: cfg.days,
    ttlSeconds: cfg.ttl_s[String(selected)] ?? 0,
  }), [selected, cfg])

  return <RangeCtx.Provider value={value}>{children}</RangeCtx.Provider>
}

export function useRange(): Ctx {
  const ctx = useContext(RangeCtx)
  if (!ctx) throw new Error('useRange must be used inside <RangeProvider>')
  return ctx
}

export const rangeLabel = (d: number): string => (d >= 30 ? '30d' : `${d}d`)
